/*
 * ChiTown Threads – desktop mega-menu hover intent (v4.5c)
 *
 * Horizon's header-menu closes or switches the open submenu the instant the
 * pointer leaves / enters a top-level <li>. A diagonal move from "Apparel"
 * toward a submenu link crosses the neighbouring items or the bare header row
 * and the menu closes. This script adds:
 *   - a grace period before a pointerleave closes the open submenu
 *     (cancelled when the pointer comes back into the menu);
 *   - a short delay before a *downward* move into a sibling item switches the
 *     open submenu (cancelled if the pointer only passes through).
 * Mouse pointers only; touch / keyboard / the mobile drawer are untouched.
 */
(() => {
  const LEAVE_GRACE_MS = 400;
  const SWITCH_DELAY_MS = 300;
  const DOWN_WINDOW_MS = 250;
  const DOWN_MIN_PX = 2;

  const synthetic = new WeakSet();
  const trail = [];

  let leaveTimer = 0;
  let leaveItem = null;
  let fireLeave = null;
  let enterTimer = 0;
  let enterItem = null;

  document.addEventListener(
    'pointermove',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      const last = trail[trail.length - 1];
      trail.push({ y: e.clientY, t: e.timeStamp });
      while (trail.length > 10) trail.shift();
      // Still heading down toward the open submenu: keep the pending close waiting.
      if (leaveItem && fireLeave && last && e.clientY > last.y) {
        clearTimeout(leaveTimer);
        leaveTimer = setTimeout(fireLeave, LEAVE_GRACE_MS);
      }
    },
    { capture: true, passive: true }
  );

  // Compare against the oldest sample in the window, or the last known position
  // when the pointer has been still (sparse pointermove samples).
  const movingDown = (e) => {
    const from = trail.find((p) => e.timeStamp - p.t <= DOWN_WINDOW_MS) || trail[trail.length - 1];
    return Boolean(from) && e.clientY - from.y >= DOWN_MIN_PX;
  };

  // Real mouse events, plus header-menu.js's own idle "reconcile" dispatch
  // (an untrusted PointerEvent with an empty pointerType).
  const isMouseLike = (e) => e.pointerType === 'mouse' || (!e.isTrusted && e.pointerType === '');

  const topLevelItem = (e) => {
    if (!(e instanceof PointerEvent) || !isMouseLike(e) || synthetic.has(e)) return null;
    const li = e.target;
    if (!(li instanceof HTMLElement) || !li.matches('header-menu .menu-list__list-item')) return null;
    if (li.slot !== '' && li.slot !== 'more') return null;
    const header = li.closest('header-component');
    if (header && header.dataset.menuStyle === 'drawer') return null;
    return li;
  };

  const activeItem = (menu) =>
    [...menu.querySelectorAll('.menu-list__list-item')].find(
      (li) =>
        (li.slot === '' || li.slot === 'more') &&
        [...li.querySelectorAll('[aria-expanded="true"]')].some((el) => !el.closest('.menu-list__submenu'))
    ) || null;

  const dispatch = (li, type, relatedTarget) => {
    const ev = new PointerEvent(type, { bubbles: false, pointerType: 'mouse', relatedTarget: relatedTarget || null });
    synthetic.add(ev);
    li.dispatchEvent(ev);
  };

  const cancelLeave = () => {
    clearTimeout(leaveTimer);
    leaveTimer = 0;
    leaveItem = null;
    fireLeave = null;
  };
  const cancelEnter = () => {
    clearTimeout(enterTimer);
    enterTimer = 0;
    enterItem = null;
  };

  window.addEventListener(
    'pointerenter',
    (e) => {
      const li = topLevelItem(e);
      if (!li) return;
      const menu = li.closest('header-menu');

      if (leaveItem) {
        const same = leaveItem === li;
        cancelLeave();
        if (same) {
          // Back inside the item whose close was pending: it is still open.
          e.stopPropagation();
          return;
        }
      }

      const active = activeItem(menu);
      if (!active || active === li) return;

      // Defer the switch when: heading down toward the open submenu, re-entering an
      // item whose switch is already pending, or header-menu.js's idle "reconcile"
      // re-dispatch (pointer at rest; it is delivered once it has stayed put).
      if (enterItem === li || movingDown(e) || !e.isTrusted) {
        e.stopPropagation();
        cancelEnter();
        enterItem = li;
        const related = e.relatedTarget;
        enterTimer = setTimeout(() => {
          enterTimer = 0;
          enterItem = null;
          if (li.matches(':hover')) dispatch(li, 'pointerenter', related);
        }, SWITCH_DELAY_MS);
      }
    },
    true
  );

  window.addEventListener(
    'pointerleave',
    (e) => {
      const li = topLevelItem(e);
      if (!li) return;

      if (enterItem === li) {
        // Passed through without activating: nothing to close.
        cancelEnter();
        e.stopPropagation();
        return;
      }

      const menu = li.closest('header-menu');
      const related = e.relatedTarget;
      e.stopPropagation();
      cancelLeave();
      leaveItem = li;
      fireLeave = () => {
        leaveTimer = 0;
        leaveItem = null;
        fireLeave = null;
        if (menu.querySelector('.menu-list__list-item:hover')) return;
        dispatch(li, 'pointerleave', related);
      };
      leaveTimer = setTimeout(fireLeave, LEAVE_GRACE_MS);
    },
    true
  );
})();
